import * as React from 'react';

import classnames from 'classnames';
import _ from 'underscore';

import {formatValue} from 'shells/entity/entity-display-values/helpers';

import {CellAsync} from './async/async';
import {CellBadge} from './badge';
import {CellCreatorOriginCategory} from './creator-origin-category';
import {CellDate} from './date';
import {CellDescription} from './description';
import {CellEmail} from './email';
import {CellEmails} from './emails';
import {CellEnum} from './enum';
import {CellLastLoggedActivity} from './last-logged-activity';
import {CellLinkedLeadDate} from './linked-lead-date';
import {CellPhone} from './phone';
import {CellPhones} from './phones';
import {CellRating} from './rating';
import {CellHierarchy} from './relationship/hierarchy';
import {CellRelationship} from './relationship/relationship';
import {CellStringsTokenized} from './relationship/strings-tokenized';
import {CellStage} from './stage';
import {CellString} from './string';
import {CellUrl} from './url';

import styles from './cell.module.css';

const CELL_COMPONENTS = {
    string: CellString,
    strings: CellStringsTokenized,
    integer: CellString,
    text: CellString,
    email: CellEmail,
    emails: CellEmails,
    stage: CellStage,
    phone: CellPhone,
    phones: CellPhones,
    boolean: CellString,
    enum: CellEnum,
    badge: CellBadge,
    number: CellString,
    currency: CellString,
    location: CellString,
    relationship: CellRelationship,
    'date-time': CellDate,
    'linked-lead-date': CellLinkedLeadDate,
    url: CellUrl,
    description: CellDescription,
    rating: CellRating,
    default: CellString,
    async: CellAsync,
    hierarchy: CellHierarchy,
    'creator-origin-category': CellCreatorOriginCategory,
} as const;

type Props = {
    cellData: any;
    columnData: any;
    entityId?: string | null | undefined;
    isHidden?: boolean;
};

// Note: PureComponent is extremely important here so that scrolling 1px doesn't
// re-render every visible cell.
export class RenderedCell extends React.PureComponent<Props> {
    render() {
        const {cellData, columnData, entityId, isHidden} = this.props;
        const formattedValue = formatValue(cellData, columnData.type, columnData.name);
        const rowType = columnData.cellType ? columnData.cellType : columnData.type;
        // Async cells won't have data but have their own loading state as they fetch
        const isLoading = _.isUndefined(cellData) && rowType !== 'async';
        if (!formattedValue && !isLoading && rowType !== 'async') {
            // CellComponent expects a value, so if we don't have one, don't render anything.
            // Async cells will have no value since the cell fetches its own value.
            return null;
        }

        let CellComponent = Object.keys(CELL_COMPONENTS).includes(rowType)
            ? // @ts-expect-error
              CELL_COMPONENTS[rowType]
            : CELL_COMPONENTS.default;

        if (columnData.name.startsWith('lastLoggedActivity')) {
            CellComponent = CellLastLoggedActivity;
        }

        const dataTip = isHidden ? getHiddenCellDataTip(columnData.name) : undefined;

        const hiddenStyleName = isHidden ? getHiddenStyleName(columnData.name) : '';

        return (
            <div data-tip={dataTip} className={classnames(styles.cell, hiddenStyleName)}>
                {isLoading ? (
                    <div className={styles['loading-bar']} />
                ) : (
                    <CellComponent
                        {...columnData}
                        value={formattedValue}
                        isHidden={isHidden}
                        entityId={entityId}
                        className={isHidden ? getHiddenStyleName(columnData.title) : ''}
                    />
                )}
            </div>
        );
    }
}

const getHiddenStyleName = (fieldName: string) => {
    switch (fieldName) {
        case 'email':
            return styles['cell--hidden-blue'];
        case 'website':
        case 'phone':
        case 'address':
            return styles['cell--hidden'];
        default:
            return '';
    }
};

const getHiddenCellDataTip = (fieldName: string): string | null | undefined => {
    const remainLockedString = 'will remain hidden until this company is unlocked.';

    switch (fieldName) {
        case 'email':
            return `Email address ${remainLockedString}`;
        case 'website':
            return `URL ${remainLockedString}`;
        case 'phone':
            return `Phone number ${remainLockedString}`;
        case 'address':
            return `Address ${remainLockedString}`;
        default:
            return undefined;
    }
};
